import { Component, OnInit, OnDestroy, ViewChild, ElementRef, signal, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { SkeletonModule } from 'primeng/skeleton';
import { MenuModule } from 'primeng/menu';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { MenuItem } from 'primeng/api';
import Chart from 'chart.js/auto';
import { Subject, takeUntil } from 'rxjs';
import { EsgHeroComponent } from '../components/esg-hero/esg-hero.component';
import { ESGScore } from '../../../shared/models/esg.model';


import { OrderAnalytics, PostAnalytics, RegionalHubAnalyticsService, RegionalHubOverview, SmartCoinOverview, UserAnalytics } from 'src/app/services/regional-analytics.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './home.page.html',
  styleUrls: ['./home.page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    ButtonModule,
    TagModule,
    TableModule,
    SkeletonModule,
    MenuModule,
    BreadcrumbModule,
    EsgHeroComponent
  ]
})
export class HomePage implements OnInit {
  private destroy$ = new Subject<void>();

  // Chart Refs
  @ViewChild('revenueChart') revenueChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('orderStatusChart') orderStatusChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('userGrowthChart') userGrowthChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('postEngagementChart') postEngagementChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('smartCoinFlowChart') smartCoinFlowChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('businessPerformanceChart') businessPerformanceChartRef!: ElementRef<HTMLCanvasElement>;

  // Breadcrumb
  home: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard' };
  breadcrumbItems: MenuItem[] = [
    { label: 'Regional Hub', routerLink: '/dashboard/regional-overview' }
  ];

  // Data
  overview: RegionalHubOverview | null = null;
  orderAnalytics: OrderAnalytics | null = null;
  userAnalytics: UserAnalytics | null = null;
  postAnalytics: PostAnalytics | null = null;
  smartCoinOverview: SmartCoinOverview | null = null;

  // Loading States
  loadingOverview = signal(true);
  loadingOrders = signal(true);
  loadingUsers = signal(true);
  loadingPosts = signal(true);
  loadingSmartCoins = signal(true);

  // State
  hasError = signal(false);
  selectedPeriod = '30d';

  // Chart data loaded flags
  revenueChartLoaded = signal(false);
  orderStatusChartLoaded = signal(false);
  userGrowthChartLoaded = signal(false);
  postEngagementChartLoaded = signal(false);
  smartCoinFlowChartLoaded = signal(false);
  businessChartLoaded = signal(false);

  // Chart has data flags
  revenueChartHasData = signal(false);
  orderStatusChartHasData = signal(false);
  userGrowthChartHasData = signal(false);
  postEngagementChartHasData = signal(false);
  smartCoinFlowChartHasData = signal(false);
  businessChartHasData = signal(false);

  // Charts
  revenueChart: Chart | null = null;
  orderStatusChart: Chart | null = null;
  userGrowthChart: Chart | null = null;
  postEngagementChart: Chart | null = null;
  smartCoinFlowChart: Chart | null = null;
  businessPerformanceChart: Chart | null = null;

  // Menu
  menuItems: MenuItem[] = [
    {
      label: 'Aktualisieren',
      icon: 'pi pi-refresh',
      command: () => this.forceRefresh()
    },
    { separator: true },
    {
      label: '7 Tage',
      icon: 'pi pi-calendar',
      command: () => this.changePeriod('7d')
    },
    {
      label: '30 Tage',
      icon: 'pi pi-calendar',
      command: () => this.changePeriod('30d')
    },
    {
      label: '90 Tage',
      icon: 'pi pi-calendar',
      command: () => this.changePeriod('90d')
    },
    {
      label: '1 Jahr',
      icon: 'pi pi-calendar',
      command: () => this.changePeriod('1y')
    }
  ];

  // Mock ESG Data
  esgScore: ESGScore = {
    environment: 78,
    social: 69,
    governance: 70
  };

  constructor(
    private analyticsService: RegionalHubAnalyticsService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadAllData(false);
  }

  ngOnDestroy(): void {
    this.destroyCharts();
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAllData(forceRefresh: boolean = false): void {
    this.resetLoadingStates(true);
    this.hasError.set(false);

    // Overview
    this.analyticsService.getOverview(this.selectedPeriod, forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.overview = data;
          this.loadingOverview.set(false);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading overview:', err);
          this.loadingOverview.set(false);
          this.hasError.set(true);
        }
      });

    // Orders
    this.analyticsService.getOrderAnalytics(this.selectedPeriod, forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.orderAnalytics = data;
          this.loadingOrders.set(false);
          this.orderStatusChartLoaded.set(true);
          setTimeout(() => this.initOrderCharts(), 100);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading orders:', err);
          this.loadingOrders.set(false);
          this.orderStatusChartLoaded.set(true);
        }
      });

    // Order Metrics for Revenue Chart
    this.analyticsService.getOrderMetrics(this.selectedPeriod, undefined, forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.revenueChartLoaded.set(true);
          setTimeout(() => this.initRevenueChart(data), 100);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading order metrics:', err);
          this.revenueChartLoaded.set(true);
        }
      });

    // Users
    this.analyticsService.getUserAnalytics(this.selectedPeriod, forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.userAnalytics = data;
          this.loadingUsers.set(false);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading users:', err);
          this.loadingUsers.set(false);
        }
      });

    // User Growth Chart
    this.analyticsService.getUserGrowth(this.selectedPeriod, forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.userGrowthChartLoaded.set(true);
          setTimeout(() => this.initUserGrowthChart(data), 100);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading user growth:', err);
          this.userGrowthChartLoaded.set(true);
        }
      });

    // Posts
    this.analyticsService.getPostAnalytics(this.selectedPeriod, forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.postAnalytics = data;
          this.loadingPosts.set(false);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading posts:', err);
          this.loadingPosts.set(false);
        }
      });

    // Post Engagement Chart
    this.analyticsService.getPostEngagement(this.selectedPeriod, forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.postEngagementChartLoaded.set(true);
          setTimeout(() => this.initPostEngagementChart(data), 100);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading post engagement:', err);
          this.postEngagementChartLoaded.set(true);
        }
      });

    // SmartCoins
    this.analyticsService.getSmartCoinOverview(this.selectedPeriod, forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.smartCoinOverview = data;
          this.loadingSmartCoins.set(false);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading SmartCoins:', err);
          this.loadingSmartCoins.set(false);
        }
      });

    // SmartCoin Flow Chart
    this.analyticsService.getSmartCoinFlow(this.selectedPeriod, forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.smartCoinFlowChartLoaded.set(true);
          setTimeout(() => this.initSmartCoinFlowChart(data), 100);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading SmartCoin flow:', err);
          this.smartCoinFlowChartLoaded.set(true);
        }
      });

    // Business Stats
    this.analyticsService.getBusinessStats(this.selectedPeriod, forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.businessChartLoaded.set(true);
          setTimeout(() => this.initBusinessPerformanceChart(data), 100);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading business stats:', err);
          this.businessChartLoaded.set(true);
        }
      });
  }

  resetLoadingStates(value: boolean): void {
    this.loadingOverview.set(value);
    this.loadingOrders.set(value);
    this.loadingUsers.set(value);
    this.loadingPosts.set(value);
    this.loadingSmartCoins.set(value);
  }

  forceRefresh(): void {
    this.destroyCharts();
    this.resetChartLoadedFlags();
    this.loadAllData(true);
  }

  changePeriod(period: string): void {
    this.selectedPeriod = period;
    this.destroyCharts();
    this.resetChartLoadedFlags();
    this.loadAllData(true);
  }

  private resetChartLoadedFlags(): void {
    this.revenueChartLoaded.set(false);
    this.orderStatusChartLoaded.set(false);
    this.userGrowthChartLoaded.set(false);
    this.postEngagementChartLoaded.set(false);
    this.smartCoinFlowChartLoaded.set(false);
    this.businessChartLoaded.set(false);
    // Reset has data flags
    this.revenueChartHasData.set(false);
    this.orderStatusChartHasData.set(false);
    this.userGrowthChartHasData.set(false);
    this.postEngagementChartHasData.set(false);
    this.smartCoinFlowChartHasData.set(false);
    this.businessChartHasData.set(false);
  }

  // ========================================
  // CHART INITIALIZATION
  // ========================================

  private destroyChart(chart: Chart | null): void {
    if (chart) {
      try {
        chart.destroy();
      } catch (err) {
        console.warn('Chart destroy failed:', err);
      }
    }
  }

  initRevenueChart(data: any): void {
    if (!this.revenueChartRef?.nativeElement || !data) {
      this.revenueChartHasData.set(false);
      return;
    }

    // Check if there's actual data
    if (!data.revenue || data.revenue.length === 0) {
      this.revenueChartHasData.set(false);
      this.revenueChart = null;
      return;
    }

    this.revenueChartHasData.set(true);
    this.destroyChart(this.revenueChart);
    this.revenueChart = null;

    const canvas = this.revenueChartRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--p-text-color') || '#64748b';
    const surfaceBorder = documentStyle.getPropertyValue('--p-content-border-color') || '#e2e8f0';

    const labels = data.revenue.map((item: any) => {
      const date = new Date(item.date);
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
    });

    this.revenueChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [{
          label: 'Umsatz (€)',
          data: data.revenue.map((item: any) => item.value),
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.08)',
          borderWidth: 2.5,
          tension: 0.4,
          fill: true,
          pointRadius: 0,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: '#10b981',
          pointHoverBorderColor: '#ffffff',
          pointHoverBorderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index'
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            titleColor: '#f1f5f9',
            bodyColor: '#cbd5e1',
            padding: 12,
            borderColor: '#334155',
            borderWidth: 1,
            displayColors: false,
            callbacks: {
              label: (context) => `${context.parsed.y!.toFixed(2)} €`
            }
          }
        },
        scales: {
          x: {
            ticks: {
              color: textColor,
              font: { size: 11 }
            },
            grid: {
              display: false
            },
            border: {
              display: false
            }
          },
          y: {
            beginAtZero: true,
            ticks: {
              color: textColor,
              font: { size: 11 },
              callback: (value) => `${value} €`
            },
            grid: {
              color: surfaceBorder,
            },
            border: {
              display: false
            }
          }
        }
      }
    });
  }

  initOrderCharts(): void {
    if (!this.orderAnalytics) return;
    this.initOrderStatusChart();
  }

  initOrderStatusChart(): void {
    if (!this.orderStatusChartRef?.nativeElement || !this.orderAnalytics) {
      this.orderStatusChartHasData.set(false);
      return;
    }

    // Check if there's actual data
    if (!this.orderAnalytics.statusBreakdown || this.orderAnalytics.statusBreakdown.length === 0) {
      this.orderStatusChartHasData.set(false);
      this.orderStatusChart = null;
      return;
    }

    this.orderStatusChartHasData.set(true);
    this.destroyChart(this.orderStatusChart);
    this.orderStatusChart = null;

    const canvas = this.orderStatusChartRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--p-text-color') || '#64748b';

    const labels = this.orderAnalytics.statusBreakdown.map(item => this.formatOrderStatus(item.status));
    const data = this.orderAnalytics.statusBreakdown.map(item => item.count);
    const colors = this.orderAnalytics.statusBreakdown.map(item => this.getStatusColor(item.status));

    this.orderStatusChart = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: labels,
        datasets: [{
          data: data,
          backgroundColor: colors,
          borderWidth: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'right',
            labels: {
              color: textColor,
              font: { size: 11 },
              usePointStyle: true,
              padding: 15
            }
          },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            titleColor: '#f1f5f9',
            bodyColor: '#cbd5e1',
            padding: 12,
            borderColor: '#334155',
            borderWidth: 1
          }
        }
      }
    });
  }

  initUserGrowthChart(data: any): void {
    if (!this.userGrowthChartRef?.nativeElement || !data) {
      this.userGrowthChartHasData.set(false);
      return;
    }

    // Check if there's actual data
    if (!data.growthChart || data.growthChart.length === 0) {
      this.userGrowthChartHasData.set(false);
      this.userGrowthChart = null;
      return;
    }

    this.userGrowthChartHasData.set(true);
    this.destroyChart(this.userGrowthChart);
    this.userGrowthChart = null;

    const canvas = this.userGrowthChartRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--p-text-color') || '#64748b';
    const surfaceBorder = documentStyle.getPropertyValue('--p-content-border-color') || '#e2e8f0';

    const labels = data.growthChart.map((item: any) => {
      const date = new Date(item.date);
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
    });

    this.userGrowthChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [{
          label: 'Neue Nutzer',
          data: data.growthChart.map((item: any) => item.newUsers),
          backgroundColor: '#8b5cf6',
          borderRadius: 6,
          borderSkipped: false
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            titleColor: '#f1f5f9',
            bodyColor: '#cbd5e1',
            padding: 12,
            borderColor: '#334155',
            borderWidth: 1,
            displayColors: false
          }
        },
        scales: {
          x: {
            ticks: {
              color: textColor,
              font: { size: 11 }
            },
            grid: {
              display: false
            },
            border: {
              display: false
            }
          },
          y: {
            beginAtZero: true,
            ticks: {
              color: textColor,
              font: { size: 11 }
            },
            grid: {
              color: surfaceBorder,
            },
            border: {
              display: false
            }
          }
        }
      }
    });
  }

  initPostEngagementChart(data: any): void {
    if (!this.postEngagementChartRef?.nativeElement || !data) {
      this.postEngagementChartHasData.set(false);
      return;
    }

    // Check if there's actual data
    if (!data.engagementChart || data.engagementChart.length === 0) {
      this.postEngagementChartHasData.set(false);
      this.postEngagementChart = null;
      return;
    }

    this.postEngagementChartHasData.set(true);
    this.destroyChart(this.postEngagementChart);
    this.postEngagementChart = null;

    const canvas = this.postEngagementChartRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--p-text-color') || '#64748b';
    const surfaceBorder = documentStyle.getPropertyValue('--p-content-border-color') || '#e2e8f0';

    const labels = data.engagementChart.map((item: any) => {
      const date = new Date(item.date);
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
    });

    this.postEngagementChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Likes',
            data: data.engagementChart.map((item: any) => item.likes),
            borderColor: '#ec4899',
            backgroundColor: 'rgba(236, 72, 153, 0.08)',
            borderWidth: 2,
            tension: 0.4,
            fill: false
          },
          {
            label: 'Kommentare',
            data: data.engagementChart.map((item: any) => item.comments),
            borderColor: '#3b82f6',
            backgroundColor: 'rgba(59, 130, 246, 0.08)',
            borderWidth: 2,
            tension: 0.4,
            fill: false
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index'
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            align: 'end',
            labels: {
              color: textColor,
              font: { size: 11 },
              usePointStyle: true,
              pointStyle: 'circle',
              padding: 15,
              boxWidth: 6,
              boxHeight: 6
            }
          },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            titleColor: '#f1f5f9',
            bodyColor: '#cbd5e1',
            padding: 12,
            borderColor: '#334155',
            borderWidth: 1
          }
        },
        scales: {
          x: {
            ticks: {
              color: textColor,
              font: { size: 11 }
            },
            grid: {
              display: false
            },
            border: {
              display: false
            }
          },
          y: {
            beginAtZero: true,
            ticks: {
              color: textColor,
              font: { size: 11 }
            },
            grid: {
              color: surfaceBorder,
            },
            border: {
              display: false
            }
          }
        }
      }
    });
  }

  initSmartCoinFlowChart(data: any): void {
    if (!this.smartCoinFlowChartRef?.nativeElement || !data) {
      this.smartCoinFlowChartHasData.set(false);
      return;
    }

    // Check if there's actual data
    if (!data.flowChart || data.flowChart.length === 0) {
      this.smartCoinFlowChartHasData.set(false);
      this.smartCoinFlowChart = null;
      return;
    }

    this.smartCoinFlowChartHasData.set(true);
    this.destroyChart(this.smartCoinFlowChart);
    this.smartCoinFlowChart = null;

    const canvas = this.smartCoinFlowChartRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--p-text-color') || '#64748b';
    const surfaceBorder = documentStyle.getPropertyValue('--p-content-border-color') || '#e2e8f0';

    const labels = data.flowChart.map((item: any) => {
      const date = new Date(item.date);
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
    });

    this.smartCoinFlowChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Verdient',
            data: data.flowChart.map((item: any) => item.earnedEuros),
            backgroundColor: '#10b981',
            borderRadius: 6,
            borderSkipped: false
          },
          {
            label: 'Eingelöst',
            data: data.flowChart.map((item: any) => -item.redeemedEuros),
            backgroundColor: '#ef4444',
            borderRadius: 6,
            borderSkipped: false
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index'
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            align: 'end',
            labels: {
              color: textColor,
              font: { size: 11 },
              usePointStyle: true,
              pointStyle: 'circle',
              padding: 15,
              boxWidth: 6,
              boxHeight: 6
            }
          },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            titleColor: '#f1f5f9',
            bodyColor: '#cbd5e1',
            padding: 12,
            borderColor: '#334155',
            borderWidth: 1,
            callbacks: {
              label: (context) => `${context.dataset.label}: ${Math.abs(context.parsed.y!).toFixed(2)} €`
            }
          }
        },
        scales: {
          x: {
            ticks: {
              color: textColor,
              font: { size: 11 }
            },
            grid: {
              display: false
            },
            border: {
              display: false
            }
          },
          y: {
            ticks: {
              color: textColor,
              font: { size: 11 },
              callback: (value) => `${value} €`
            },
            grid: {
              color: surfaceBorder,
            },
            border: {
              display: false
            }
          }
        }
      }
    });
  }

  initBusinessPerformanceChart(data: any): void {
    if (!this.businessPerformanceChartRef?.nativeElement || !data) {
      this.businessChartHasData.set(false);
      return;
    }

    // Check if there's actual data
    if (!data.businesses || data.businesses.length === 0) {
      this.businessChartHasData.set(false);
      this.businessPerformanceChart = null;
      return;
    }

    this.businessChartHasData.set(true);
    this.destroyChart(this.businessPerformanceChart);
    this.businessPerformanceChart = null;

    const canvas = this.businessPerformanceChartRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--p-text-color') || '#64748b';
    const surfaceBorder = documentStyle.getPropertyValue('--p-content-border-color') || '#e2e8f0';

    // Take top 10 businesses
    const topBusinesses = data.businesses.slice(0, 10);
    const labels = topBusinesses.map((b: any) => b.name);

    this.businessPerformanceChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [{
          label: 'Umsatz (€)',
          data: topBusinesses.map((b: any) => b.revenue),
          backgroundColor: '#6366f1',
          borderRadius: 6,
          borderSkipped: false
        }]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            titleColor: '#f1f5f9',
            bodyColor: '#cbd5e1',
            padding: 12,
            borderColor: '#334155',
            borderWidth: 1,
            displayColors: false,
            callbacks: {
              label: (context) => `${context.parsed.x.toFixed(2)} €`
            }
          }
        },
        scales: {
          x: {
            beginAtZero: true,
            ticks: {
              color: textColor,
              font: { size: 11 },
              callback: (value) => `${value} €`
            },
            grid: {
              color: surfaceBorder,
            },
            border: {
              display: false
            }
          },
          y: {
            ticks: {
              color: textColor,
              font: { size: 11 }
            },
            grid: {
              display: false
            },
            border: {
              display: false
            }
          }
        }
      }
    });
  }

  destroyCharts(): void {
    this.destroyChart(this.revenueChart);
    this.destroyChart(this.orderStatusChart);
    this.destroyChart(this.userGrowthChart);
    this.destroyChart(this.postEngagementChart);
    this.destroyChart(this.smartCoinFlowChart);
    this.destroyChart(this.businessPerformanceChart);

    this.revenueChart = null;
    this.orderStatusChart = null;
    this.userGrowthChart = null;
    this.postEngagementChart = null;
    this.smartCoinFlowChart = null;
    this.businessPerformanceChart = null;
  }

  // ========================================
  // HELPER METHODS
  // ========================================

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  }

  formatOrderStatus(status: string): string {
    const statusMap: Record<string, string> = {
      'Pending': 'Ausstehend',
      'Confirmed': 'Bestätigt',
      'Ready for Pickup': 'Abholbereit',
      'Out for Delivery': 'Unterwegs',
      'Completed': 'Abgeschlossen',
      'Cancelled': 'Storniert'
    };
    return statusMap[status] || status;
  }

  getStatusColor(status: string): string {
    const colorMap: Record<string, string> = {
      'Pending': '#f59e0b',
      'Confirmed': '#3b82f6',
      'Ready for Pickup': '#8b5cf6',
      'Out for Delivery': '#6366f1',
      'Completed': '#10b981',
      'Cancelled': '#ef4444'
    };
    return colorMap[status] || '#64748b';
  }

  getTrendClass(trend: number): string {
    if (trend > 0) return 'text-emerald-600';
    if (trend < 0) return 'text-red-600';
    return 'text-slate-600';
  }

  getTrendIcon(trend: number): string {
    if (trend > 0) return 'pi-arrow-up';
    if (trend < 0) return 'pi-arrow-down';
    return 'pi-minus';
  }

  onEsgCardClick(): void {
    this.router.navigate(['/dashboard/esg/environment']);
  }

  onEsgCategoryClick(category: 'environment' | 'social' | 'governance'): void {
    this.router.navigate([`/dashboard/esg/${category}`]);
  }
}
